import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import useUIStore from '@/app/store/ui.store';
import { useTranslation } from '@/shared/i18n';
import { useIsInterim } from '@/shared/hooks/useTenant';
import { useAuth } from '@/shared/hooks/useAuth';
import { useInboxCount } from '@/shared/hooks/useInboxCount';
import { usePermissionCheck, useModuleAvailable } from '@/shared/hooks/usePermissionCheck';
import { cn } from '@/shared/utils/helpers/cn';

import {
  HomeIcon,
  InboxIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ClockIcon,
  DocumentTextIcon,
  FolderIcon,
  BriefcaseIcon,
  UserGroupIcon,
  DocumentDuplicateIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  CurrencyDollarIcon,
  RocketLaunchIcon,
  BuildingOffice2Icon,
  UsersIcon,
  CreditCardIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  PhoneIcon,
  ServerIcon,
  DocumentMagnifyingGlassIcon,
  TagIcon,
  ReceiptPercentIcon,
  CogIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Bars3Icon,
  ShieldCheckIcon,
  KeyIcon,
  SunIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  badge?: number;
  children?: NavigationItem[];
  permission?: { module: string; feature: string };
  moduleAvailable?: string;
}

interface NavigationSection {
  label?: string;
  items: NavigationItem[];
}

const useNavigation = (): NavigationSection[] => {
  const { t } = useTranslation();
  const isInterim = useIsInterim();
  const { adminContext } = useAuth();
  const isAdmin = adminContext?.isAdmin ?? false;
  const { data: inboxData } = useInboxCount();

  const sections: NavigationSection[] = [
    {
      items: [
        { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon },
        { name: t('navigation.inbox'), href: '/inbox', icon: InboxIcon, badge: inboxData?.pendingApprovals || undefined, permission: { module: 'workflow', feature: 'approval-read' } },
      ],
    },
    {
      items: [
        { name: t('navigation.myLeave'), href: '/my/leave', icon: CalendarDaysIcon, moduleAvailable: 'leave_mgmt' },
        { name: t('navigation.myExpenses'), href: '/my/expenses', icon: BanknotesIcon, moduleAvailable: 'expenses' },
        { name: t('navigation.myTimesheets'), href: '/my/timesheets', icon: ClockIcon },
        { name: t('navigation.myPayslips'), href: '/my/payslips', icon: DocumentTextIcon },
        { name: t('navigation.myDocuments'), href: '/my/documents', icon: FolderIcon },
        ...(isInterim ? [{ name: t('navigation.myMission'), href: '/my/mission', icon: BriefcaseIcon, moduleAvailable: 'interim' }] : []),
      ],
    },
    {
      label: t('navigation.team'),
      items: [
        { name: t('navigation.teamDashboard'), href: '/team', icon: UserGroupIcon, permission: { module: 'hr', feature: 'employee-read' } },
        { name: t('navigation.teamLeave'), href: '/team/leave', icon: CalendarDaysIcon, permission: { module: 'leave_mgmt', feature: 'leave-approve' } },
        { name: t('navigation.teamTimesheets'), href: '/team/timesheets', icon: ClockIcon, permission: { module: 'hr', feature: 'timesheet-read' } },
        { name: t('navigation.teamExpenses'), href: '/team/expenses', icon: BanknotesIcon, permission: { module: 'expenses', feature: 'expense-approve' } },
        ...(isInterim ? [
          { name: t('navigation.teamMissions'), href: '/team/missions', icon: BriefcaseIcon, moduleAvailable: 'interim' },
          { name: t('navigation.teamClients'), href: '/team/clients', icon: BuildingOfficeIcon, moduleAvailable: 'interim' },
        ] : []),
      ],
    },
    {
      label: t('navigation.hr'),
      items: [
        { name: t('navigation.employees'), href: '/hr/employees', icon: UserGroupIcon, permission: { module: 'hr_core', feature: 'employee-write' } },
        { name: 'Recrutement', href: '/hr/recruitment', icon: UserGroupIcon, permission: { module: 'hr', feature: 'recruitment.view' } },
        { name: t('navigation.contracts'), href: '/hr/contracts', icon: DocumentDuplicateIcon, moduleAvailable: 'hr_core' },
        { name: t('navigation.departments'), href: '/hr/departments', icon: BuildingOfficeIcon, moduleAvailable: 'hr_core' },
        { name: t('navigation.positions'), href: '/hr/positions', icon: IdentificationIcon, moduleAvailable: 'hr_core' },
        { name: t('navigation.payroll'), href: '/hr/payroll', icon: CurrencyDollarIcon, permission: { module: 'payroll', feature: 'payroll-read' }, moduleAvailable: 'payroll' },
        { name: t('navigation.payrollConfig', 'Config. paie'), href: '/hr/payroll/config', icon: CogIcon, permission: { module: 'payroll', feature: 'payroll-write' }, moduleAvailable: 'payroll' },
        { name: t('navigation.planning', 'Planning'), href: '/hr/planning', icon: CalendarDaysIcon, permission: { module: 'hr', feature: 'planning-read' }, moduleAvailable: 'time_mgmt' },
        { name: t('navigation.leaveTypes', 'Types de congé'), href: '/hr/leave-types', icon: TagIcon, permission: { module: 'hr', feature: 'leave-config-read' }, moduleAvailable: 'leave_mgmt' },
        { name: t('navigation.leavePolicies', 'Politiques'), href: '/hr/leave-policies', icon: ShieldCheckIcon, permission: { module: 'hr', feature: 'leave-config-read' }, moduleAvailable: 'leave_mgmt' },
        { name: t('navigation.publicHolidays', 'Jours fériés'), href: '/hr/public-holidays', icon: SunIcon, permission: { module: 'hr', feature: 'leave-config-read' }, moduleAvailable: 'leave_mgmt' },
        { name: t('navigation.leaveBalances', 'Soldes congés'), href: '/hr/leave-balances', icon: ChartBarIcon, permission: { module: 'hr', feature: 'leave-balance-read' }, moduleAvailable: 'leave_mgmt' },
        ...(isInterim ? [
          { name: t('navigation.missions'), href: '/hr/missions', icon: RocketLaunchIcon, permission: { module: 'hr', feature: 'mission-read' }, moduleAvailable: 'interim' },
          { name: t('navigation.clients'), href: '/hr/clients', icon: BuildingOffice2Icon, permission: { module: 'hr', feature: 'client-read' }, moduleAvailable: 'interim' },
          { name: t('navigation.interimaires'), href: '/hr/interimaires', icon: UsersIcon, permission: { module: 'hr', feature: 'interim-read' }, moduleAvailable: 'interim' },
        ] : []),
      ],
    },
    {
      label: t('navigation.payments', 'Payments'),
      items: [
        { name: t('navigation.paymentDashboard', 'Accueil'), href: '/payments', icon: HomeIcon, permission: { module: 'payments', feature: 'request-read' } },
        { name: t('navigation.paymentTransfer', 'Transfert'), href: '/payments/transfer', icon: ArrowsRightLeftIcon, permission: { module: 'payments', feature: 'request-write' } },
        { name: t('navigation.paymentTasks', 'Mes taches'), href: '/payments/tasks', icon: CreditCardIcon, permission: { module: 'payments', feature: 'request-read' } },
        { name: t('navigation.paymentAccounts', 'Mes comptes'), href: '/payments/accounts', icon: BanknotesIcon, permission: { module: 'payments', feature: 'request-read' } },
        { name: t('navigation.beneficiaries', 'Beneficiaires'), href: '/payments/beneficiaries', icon: UsersIcon, permission: { module: 'payments', feature: 'beneficiary-read' } },
      ],
    },
    {
      label: t('navigation.communication', 'Communication'),
      items: [
        { name: t('navigation.campaigns', 'Campaigns'), href: '/communication/campaigns', icon: MegaphoneIcon, permission: { module: 'communication', feature: 'campaign-read' } },
        { name: t('navigation.templates', 'Templates'), href: '/communication/templates', icon: EnvelopeIcon, permission: { module: 'communication', feature: 'template-read' } },
        { name: t('navigation.contacts', 'Contacts'), href: '/communication/contacts', icon: PhoneIcon, permission: { module: 'communication', feature: 'contact-read' } },
        { name: t('navigation.providers', 'Providers'), href: '/communication/providers', icon: ServerIcon, permission: { module: 'communication', feature: 'provider-read' } },
      ],
    },
    {
      label: t('navigation.ged', 'Documents'),
      items: [
        { name: t('navigation.documents', 'Documents'), href: '/ged/documents', icon: DocumentMagnifyingGlassIcon, permission: { module: 'ged', feature: 'document-read' } },
        { name: t('navigation.categories', 'Categories'), href: '/ged/categories', icon: TagIcon, permission: { module: 'ged', feature: 'category-read' } },
      ],
    },
    {
      label: t('navigation.billing', 'Billing'),
      items: [
        { name: t('navigation.invoices', 'Invoices'), href: '/billing/invoices', icon: ReceiptPercentIcon, permission: { module: 'billing', feature: 'invoice-read' } },
      ],
    },
    {
      label: t('navigation.workflows', 'Workflows'),
      items: [
        { name: t('navigation.configurations', 'Configurations'), href: '/workflows/configurations', icon: CogIcon, permission: { module: 'workflow', feature: 'config-read' } },
        { name: t('navigation.instances', 'Instances'), href: '/workflows/instances', icon: ArrowPathIcon, permission: { module: 'workflow', feature: 'instance-read' } },
      ],
    },
    ...(isAdmin ? [{
      label: t('navigation.administration', 'Administration'),
      items: [
        { name: t('navigation.adminUsers', 'Utilisateurs'), href: '/admin/users', icon: UserGroupIcon, permission: { module: 'admin', feature: 'user.view' } },
        { name: t('navigation.adminTenants', 'Tenants'), href: '/admin/tenants', icon: BuildingOffice2Icon, permission: { module: 'admin', feature: 'tenant.view' } },
        { name: t('navigation.adminProfiles', 'Profils'), href: '/admin/profiles', icon: ShieldCheckIcon, permission: { module: 'admin', feature: 'profile.view' } },
        { name: t('navigation.adminPermissions', 'Permissions'), href: '/admin/permissions', icon: KeyIcon, permission: { module: 'admin', feature: 'permission.view' } },
        { name: t('navigation.adminModules', 'Modules'), href: '/admin/modules', icon: ServerIcon, permission: { module: 'admin', feature: 'module.view' } },
        { name: t('navigation.adminLicences', 'Licences'), href: '/admin/licences', icon: CreditCardIcon, permission: { module: 'admin', feature: 'licence.view' } },
        { name: 'Keycloak', href: '/admin/keycloak', icon: ShieldCheckIcon, permission: { module: 'admin', feature: 'keycloak.sync' } },
      ],
    }] : []),
  ];

  return sections;
};

// Navigation item component
interface NavItemProps {
  item: NavigationItem;
  isCollapsed: boolean;
  onLinkClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ item, isCollapsed, onLinkClick }) => {
  return (
    <NavLink
      to={item.href}
      onClick={onLinkClick}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
          isActive
            ? 'bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/50 hover:text-neutral-900 dark:hover:text-white'
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active indicator bar */}
          {isActive && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full bg-primary-600 dark:bg-primary-400" />
          )}
          <item.icon
            className={cn(
              'flex-shrink-0 w-5 h-5 transition-colors duration-200',
              isCollapsed ? 'mr-0' : 'mr-3',
              isActive ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-400 dark:text-neutral-500 group-hover:text-neutral-600 dark:group-hover:text-neutral-300'
            )}
          />
          {!isCollapsed && (
            <>
              <span className="flex-1 truncate">{item.name}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className="ml-2 min-w-[1.25rem] h-5 flex items-center justify-center px-1.5 text-xs font-bold bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-full shadow-sm shadow-red-500/25">
                  {item.badge}
                </span>
              )}
            </>
          )}
          {isCollapsed && item.badge !== undefined && item.badge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white dark:ring-neutral-800" />
          )}
        </>
      )}
    </NavLink>
  );
};

// Permission-gated navigation item wrapper
const PermissionNavItem: React.FC<{ item: NavigationItem; children: React.ReactNode }> = ({ item, children }) => {
  const permCheck = usePermissionCheck(item.permission ?? { module: '', feature: '' });
  const modCheck = useModuleAvailable(item.moduleAvailable ?? '');

  const hasAccess = (!item.permission || permCheck) && (!item.moduleAvailable || modCheck);
  if (!hasAccess) return null;
  return <>{children}</>;
};

// Section header for collapsible groups
interface SectionProps {
  label: string;
  items: NavigationItem[];
  isCollapsed: boolean;
  onLinkClick: () => void;
}

const NavSection: React.FC<SectionProps> = ({ label, items, isCollapsed, onLinkClick }) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(() => {
    return items.some((item) => location.pathname.startsWith(item.href));
  });

  if (isCollapsed) {
    return (
      <div className="space-y-0.5">
        {items.map((item) => (
          <PermissionNavItem key={item.href} item={item}>
            <NavItem item={item} isCollapsed={isCollapsed} onLinkClick={onLinkClick} />
          </PermissionNavItem>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center w-full px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors duration-200"
      >
        <span className="flex-1 text-left">{label}</span>
        <ChevronDownIcon
          className={cn(
            'w-3.5 h-3.5 transition-transform duration-200',
            isExpanded ? 'rotate-180' : ''
          )}
        />
      </button>

      {isExpanded && (
        <div className="mt-0.5 space-y-0.5 animate-fade-in">
          {items.map((item) => (
            <PermissionNavItem key={item.href} item={item}>
              <NavItem item={item} isCollapsed={isCollapsed} onLinkClick={onLinkClick} />
            </PermissionNavItem>
          ))}
        </div>
      )}
    </div>
  );
};

export const Sidebar: React.FC = () => {
  const { isSidebarCollapsed, toggleSidebarCollapsed, setSidebarOpen } = useUIStore();
  const sections = useNavigation();

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="h-full bg-white dark:bg-neutral-800 border-r border-neutral-200/80 dark:border-neutral-700/80 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-neutral-100 dark:border-neutral-700/50">
        {!isSidebarCollapsed && (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm shadow-primary-500/25">
              <span className="text-white font-bold text-sm">GF</span>
            </div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-indigo-600 bg-clip-text text-transparent">
              GoFlux
            </h1>
          </div>
        )}

        {isSidebarCollapsed && (
          <div className="w-8 h-8 mx-auto bg-gradient-to-br from-primary-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm shadow-primary-500/25">
            <span className="text-white font-bold text-sm">GF</span>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:text-neutral-300 dark:hover:bg-neutral-700 transition-all duration-200"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>

          {!isSidebarCollapsed && (
            <button
              onClick={toggleSidebarCollapsed}
              className="hidden lg:flex p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:text-neutral-300 dark:hover:bg-neutral-700 transition-all duration-200"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expand button when collapsed */}
      {isSidebarCollapsed && (
        <button
          onClick={toggleSidebarCollapsed}
          className="hidden lg:flex mx-auto mt-2 p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:text-neutral-300 dark:hover:bg-neutral-700 transition-all duration-200"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 space-y-3 overflow-y-auto">
        {sections.map((section, index) => {
          if (section.label) {
            return (
              <NavSection
                key={section.label}
                label={section.label}
                items={section.items}
                isCollapsed={isSidebarCollapsed}
                onLinkClick={handleLinkClick}
              />
            );
          }
          return (
            <div key={index} className="space-y-0.5">
              {section.items.map((item) => (
                <PermissionNavItem key={item.href} item={item}>
                  <NavItem
                    item={item}
                    isCollapsed={isSidebarCollapsed}
                    onLinkClick={handleLinkClick}
                  />
                </PermissionNavItem>
              ))}
            </div>
          );
        })}
      </nav>
    </div>
  );
};
